import { useState, useEffect } from 'react'
import {
    getAllProjects,
    getProjectBySlug,
    getAllExperiences,
    getAllTechnologies,
    getAllEducation,
    getAllBlogPosts,
    getBlogPostBySlug,
    getSiteSettings,
    getFeaturedProjects,
    getProjectsByTechnology,
    getProjectsByType,
    getBlockchainExperiences,
    getTechnologiesByCategory,
    getBlockchainTechnologies,
    getBlogPostsByCategory,
    searchProjects,
    searchBlogPosts
} from './api'
import {
    Project,
    Experience,
    Technology,
    Education,
    BlogPost,
    SiteSettings
} from '@/types'

/**
 * Custom hook for handling API request states
 */
interface UseApiRequestState<T> {
    data: T | null
    isLoading: boolean
    error: Error | null
}

interface UseApiRequestOptions {
    initialFetch?: boolean
}

/**
 * Generic hook for handling API requests with loading and error states
 */
function useApiRequest<T>(
    fetchFn: () => Promise<T>,
    options: UseApiRequestOptions = { initialFetch: true }
): [UseApiRequestState<T>, () => Promise<T | void>] {
    const [state, setState] = useState<UseApiRequestState<T>>({
        data: null,
        isLoading: options.initialFetch ?? true,
        error: null,
    })

    const fetchData = async () => {
        setState(prev => ({ ...prev, isLoading: true, error: null }))

        try {
            const data = await fetchFn()
            setState({ data, isLoading: false, error: null })
            return data
        } catch (error) {
            const errorObj = error instanceof Error ? error : new Error('An unknown error occurred')
            setState({ data: null, isLoading: false, error: errorObj })
            throw errorObj
        }
    }

    useEffect(() => {
        if (options.initialFetch) {
            fetchData()
        }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    return [state, fetchData]
}

/**
 * Hook for fetching all projects
 */
export function useProjects(initialFetch = true) {
    return useApiRequest<Project[]>(() => getAllProjects(), { initialFetch })
}

/**
 * Hook for fetching a single project by slug
 */
export function useProject(slug: string, initialFetch = true) {
    return useApiRequest<Project>(() => getProjectBySlug(slug), { initialFetch })
}

/**
 * Hook for fetching all experiences
 */
export function useExperiences(initialFetch = true) {
    return useApiRequest<Experience[]>(() => getAllExperiences(), { initialFetch })
}

/**
 * Hook for fetching all technologies/skills
 */
export function useTechnologies(initialFetch = true) {
    return useApiRequest<Technology[]>(() => getAllTechnologies(), { initialFetch })
}

/**
 * Hook for fetching all education entries
 */
export function useEducation(initialFetch = true) {
    return useApiRequest<Education[]>(() => getAllEducation(), { initialFetch })
}

/**
 * Hook for fetching all blog posts
 */
export function useBlogPosts(initialFetch = true) {
    return useApiRequest<BlogPost[]>(() => getAllBlogPosts(), { initialFetch })
}

/**
 * Hook for fetching a single blog post by slug
 */
export function useBlogPost(slug: string, initialFetch = true) {
    return useApiRequest<BlogPost>(() => getBlogPostBySlug(slug), { initialFetch })
}

/**
 * Hook for fetching site settings
 */
export function useSiteSettings(initialFetch = true) {
    return useApiRequest<SiteSettings>(() => getSiteSettings(), { initialFetch })
}/**
 * Hook for fetching featured projects
 */
export function useFeaturedProjects(limit = 3, initialFetch = true) {
    return useApiRequest<Project[]>(() => getFeaturedProjects(limit), { initialFetch })
}

/**
 * Hook for fetching projects by technology
 */
export function useProjectsByTechnology(technologyId: string, initialFetch = true) {
    return useApiRequest<Project[]>(() => getProjectsByTechnology(technologyId), { initialFetch })
}

/**
 * Hook for fetching projects by type
 */
export function useProjectsByType(projectType: string, initialFetch = true) {
    return useApiRequest<Project[]>(() => getProjectsByType(projectType), { initialFetch })
}

/**
 * Hook for fetching blockchain-specific experiences
 */
export function useBlockchainExperiences(initialFetch = true) {
    return useApiRequest<Experience[]>(() => getBlockchainExperiences(), { initialFetch })
}

/**
 * Hook for fetching technologies by category
 */
export function useTechnologiesByCategory(category: string, initialFetch = true) {
    return useApiRequest<Technology[]>(() => getTechnologiesByCategory(category), { initialFetch })
}

/**
 * Hook for fetching blockchain-specific technologies
 */
export function useBlockchainTechnologies(initialFetch = true) {
    return useApiRequest<Technology[]>(() => getBlockchainTechnologies(), { initialFetch })
}

/**
 * Hook for fetching blog posts by category
 */
export function useBlogPostsByCategory(category: string, initialFetch = true) {
    return useApiRequest<BlogPost[]>(() => getBlogPostsByCategory(category), { initialFetch })
}

/**
 * Hook for searching projects
 */
export function useSearchProjects(query: string, initialFetch = true) {
    return useApiRequest<Project[]>(() => searchProjects(query), { initialFetch })
}

/**
 * Hook for searching blog posts
 */
export function useSearchBlogPosts(query: string, initialFetch = true) {
    return useApiRequest<BlogPost[]>(() => searchBlogPosts(query), { initialFetch })
}